import { useMemo, useState } from "react"
import { Drawer, Empty, Segmented, Select, Table, Tag, Tooltip } from "antd"
import { Info } from "lucide-react"
import type { TableColumnsType } from "antd"
import { useQuery } from "@tanstack/react-query"
import type { CollectionTask } from "@csbi/contracts"
import { formatTimestamp } from "@csbi/viz-core"
import { fetchRunDetail } from "../../../features/tiles/api.js"
import { ENV_LABELS, ENV_ORDER, envLabel, envRank } from "../../../shared/env-labels.js"

export const REJECTED_MEANING =
  "A non-zero value means srv returned a metric id that is not in the catalog — the two sides' contracts have started to diverge."

/**
 * ⚠ It also explains the word "deployment". The whole app (sidebar, registry, this page) says **deployment**
 * rather than **customer**, because the two are not always 1:1: usually one customer is one regdesk-srv, but a
 * customer running both dev and production is **two deployments, two rows and two sets of data** to us.
 * Without this sentence, "1 deployment" reads as "1 customer", and that equality does not always hold.
 */
export const RUN_SCOPE_MEANING =
  "A deployment is one registered regdesk-srv instance — usually one per customer, but a customer running " +
  "separate dev and production instances counts as two. One run collects every deployment that has collection " +
  "switched on, all 14 modules each. Open the run to see the per-deployment breakdown."

const PARTIAL_MEANING =
  "srv hit its result limit for a ranking metric, so it returned only the top N rows. The numbers from this " +
  "task are a partial list, not the whole population."

export const InfoHint = ({ text }: { text: string }) => (
  <Tooltip title={text}>
    <Info size={13} style={{ color: "var(--csbi-ink-muted)", flex: "none" }} />
  </Tooltip>
)

export const withHint = (label: string, text: string) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
    {label}
    <InfoHint text={text} />
  </span>
)

const STATE_COLOR: Record<CollectionTask["state"], string> = {
  ok: "default",
  unsupported: "warning",
  error: "error",
  skipped: "default",
}

/** Colours for the environment tags. **production is the only one with colour** — when something breaks it has to jump out */
const ENV_COLOR: Record<string, string | undefined> = {
  production: "blue",
  staging: undefined,
  dev: undefined,
}

const num = (n: number | null) => (
  <span style={{ fontVariantNumeric: "tabular-nums" }}>{n == null ? "—" : n.toLocaleString()}</span>
)

const isProblem = (t: CollectionTask) => t.state === "error" || t.pointsRejected > 0 || t.truncated

const columns: TableColumnsType<CollectionTask> = [
  {
    /**
     * ⚠ This column shows the **display name + environment**, not a bare tenant_id.
     */
    title: "Deployment",
    dataIndex: "displayName",
    key: "tenantId",
    width: 210,
    sorter: (a, b) =>
      envRank(a.environment) - envRank(b.environment) || a.displayName.localeCompare(b.displayName),
    /**
     * ⚠ **The tenant id is no longer shown as small text under the name.**
     */
    render: (name: string, t) => (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <Tooltip title={name === t.tenantId ? undefined : `id: ${t.tenantId}`}>
          <span>{name}</span>
        </Tooltip>
        <Tag color={ENV_COLOR[t.environment ?? ""]} style={{ marginInlineEnd: 0, fontSize: 11, lineHeight: "16px" }}>
          {envLabel(t.environment)}
        </Tag>
      </span>
    ),
  },
  { title: "Module", dataIndex: "srvModule", key: "srvModule", width: 130 },
  {
    title: "State",
    dataIndex: "state",
    key: "state",
    width: 110,
    render: (s: CollectionTask["state"]) => <Tag color={STATE_COLOR[s]}>{s}</Tag>,
  },
  { title: "HTTP", dataIndex: "httpStatus", key: "httpStatus", width: 70, align: "right", render: num },
  {
    title: "Took",
    dataIndex: "durationMs",
    key: "durationMs",
    width: 90,
    align: "right",
    sorter: (a, b) => (a.durationMs ?? -1) - (b.durationMs ?? -1),
    render: (ms: number | null) =>
      ms == null ? "—" : <span style={{ fontVariantNumeric: "tabular-nums" }}>{`${(ms / 1000).toFixed(1)}s`}</span>,
  },
  {
    /**
     * ⚠ This column is called Requests, not Tries.
     */
    title: withHint("Requests", "HTTP calls this task made, including one per time-window chunk. Not a retry count."),
    dataIndex: "attempts",
    key: "attempts",
    width: 90,
    align: "right",
    render: num,
  },
  { title: "Written", dataIndex: "pointsWritten", key: "pointsWritten", width: 90, align: "right", render: num },
  {
    title: withHint("Rejected", REJECTED_MEANING),
    dataIndex: "pointsRejected",
    key: "pointsRejected",
    width: 115,
    align: "right",
    render: (n: number) =>
      n > 0 ? (
        <Tooltip title={`${REJECTED_MEANING} This one needs a person.`}>
          <Tag color="error" style={{ marginInlineEnd: 0 }}>
            {n.toLocaleString()}
          </Tag>
        </Tooltip>
      ) : (
        num(n)
      ),
  },
  {
    /**
     * ⚠ This column is called Details, not "Why it failed".
     */
    title: "Details",
    key: "details",
    render: (_: unknown, t) =>
      t.errorCode ? (
        <span>
          <Tag color="error">{t.errorCode}</Tag>
          <span style={{ color: "var(--csbi-ink-secondary)" }}>{t.errorMsg}</span>
        </span>
      ) : t.truncated ? (
        /**
         * ⚠ The tag is `partial`, not `truncated`.
         */
        <Tooltip title={PARTIAL_MEANING}>
          <Tag color="warning">partial</Tag>
        </Tooltip>
      ) : (
        <span style={{ color: "var(--csbi-ink-muted)" }}>—</span>
      ),
  },
]

function deploymentOptions(tasks: readonly CollectionTask[]) {
  const byEnv = new Map<string, Map<string, string>>()
  for (const t of tasks) {
    const key = t.environment ?? "unknown"
    let group = byEnv.get(key)
    if (!group) byEnv.set(key, (group = new Map()))
    group.set(t.tenantId, t.displayName)
  }
  return [...byEnv.entries()]
    .sort((a, b) => envRank(a[0] === "unknown" ? null : a[0]) - envRank(b[0] === "unknown" ? null : b[0]))
    .map(([env, group]) => ({
      label: env === "unknown" ? "Unknown" : (ENV_LABELS[env as keyof typeof ENV_LABELS] ?? env),
      options: [...group.entries()]
        .sort((a, b) => a[1].localeCompare(b[1]))
        .map(([id, name]) => ({ value: id, label: name })),
    }))
}

export function RunDetailDrawer({ runId, onClose }: { runId: string | null; onClose: () => void }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["ops", "runs", runId],
    queryFn: ({ signal }) => fetchRunDetail(runId!, signal),
    enabled: runId != null,
  })

  const [deployments, setDeployments] = useState<string[]>([])
  const [environments, setEnvironments] = useState<string[]>([])
  const [modules, setModules] = useState<string[]>([])
  const [scope, setScope] = useState<"all" | "problems">("all")

  const tasks = useMemo(() => data?.tasks ?? [], [data])
  const tenantOptions = useMemo(() => deploymentOptions(tasks), [tasks])
  const envOptions = useMemo(
    () =>
      [...new Set(tasks.map((t) => t.environment ?? "unknown"))]
        .sort((a, b) => envRank(a === "unknown" ? null : a) - envRank(b === "unknown" ? null : b))
        .map((e) => ({ value: e, label: e === "unknown" ? "Unknown" : envLabel(e) })),
    [tasks],
  )
  const moduleOptions = useMemo(
    () => [...new Set(tasks.map((t) => t.srvModule))].sort().map((m) => ({ value: m, label: m })),
    [tasks],
  )

  /** The three dropdowns narrow first, and the trouble toggle counts against the narrowed set — otherwise the count disagrees with the table in front of you */
  const selected = useMemo(
    () =>
      tasks.filter(
        (t) =>
          (deployments.length === 0 || deployments.includes(t.tenantId)) &&
          (environments.length === 0 || environments.includes(t.environment ?? "unknown")) &&
          (modules.length === 0 || modules.includes(t.srvModule)),
      ),
    [tasks, deployments, environments, modules],
  )
  const problems = useMemo(() => selected.filter(isProblem), [selected])
  const rows = scope === "problems" && problems.length > 0 ? problems : selected

  const reset = () => {
    setDeployments([])
    setEnvironments([])
    setModules([])
    setScope("all")
  }

  /**
   * ⚠ **The filters sit above the table, not in antd's column-header funnels.**
   */
  const filtersActive =
    deployments.length > 0 || environments.length > 0 || modules.length > 0 || scope !== "all"

  const run = data?.run
  return (
    <Drawer
      open={runId != null}
      onClose={onClose}
      width={1080}
      afterOpenChange={(open) => !open && reset()}
      title={run ? `Collection run · ${formatTimestamp(run.startedAt)} · ${run.trigger}` : "Collection run"}
    >
      {error ? (
        <div style={{ color: "var(--csbi-ink-secondary)" }}>{(error as Error).message}</div>
      ) : (
        <>
          {run && (
            <div style={{ marginBottom: 12, color: "var(--csbi-ink-secondary)", fontSize: 13 }}>
              {run.tenantsTotal} deployment{run.tenantsTotal === 1 ? "" : "s"} · {run.tasksOk}/{run.tasksTotal} tasks
              succeeded
              {run.tasksSkipped > 0 && ` · ${run.tasksSkipped} skipped`} · {run.pointsWritten.toLocaleString()} points
              written
              {run.pointsRejected > 0 && (
                <strong style={{ color: "var(--csbi-status-critical-ink, #c0392b)" }}>
                  {" "}
                  · {run.pointsRejected.toLocaleString()} rejected
                </strong>
              )}
              {run.requestedBy && ` · requested by ${run.requestedBy}`}
            </div>
          )}

          {tasks.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
              {/*
                ⚠ **All three dropdowns are always present**, even with one deployment and one environment.
                */}
              <Select
                mode="multiple"
                size="small"
                allowClear
                style={{ minWidth: 210 }}
                placeholder="All deployments"
                options={tenantOptions}
                value={deployments}
                onChange={setDeployments}
                maxTagCount="responsive"
              />
              <Select
                mode="multiple"
                size="small"
                allowClear
                style={{ minWidth: 150 }}
                placeholder="All environments"
                options={envOptions}
                value={environments}
                onChange={setEnvironments}
                maxTagCount="responsive"
              />
              <Select
                mode="multiple"
                size="small"
                allowClear
                style={{ minWidth: 180 }}
                placeholder="All modules"
                options={moduleOptions}
                value={modules}
                onChange={setModules}
                maxTagCount="responsive"
              />
              <Segmented
                size="small"
                value={problems.length === 0 ? "all" : scope}
                onChange={(v) => setScope(v as "all" | "problems")}
                options={[
                  { label: `All ${selected.length}`, value: "all" },
                  { label: `Problems ${problems.length}`, value: "problems", disabled: problems.length === 0 },
                ]}
              />
              {filtersActive && (
                <a onClick={reset} style={{ fontSize: 12 }}>
                  Clear
                </a>
              )}
            </div>
          )}

          <Table<CollectionTask>
            size="small"
            rowKey={(t) => `${t.tenantId}:${t.srvModule}`}
            loading={isLoading}
            columns={columns}
            dataSource={rows}
            pagination={false}
            scroll={{ x: "max-content" }}
            locale={{
              emptyText: isLoading ? (
                " "
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={filtersActive ? "Nothing matches these filters." : "This run recorded no tasks."}
                />
              ),
            }}
          />
        </>
      )}
    </Drawer>
  )
}

export { ENV_ORDER }
